// The accepted word's sequence (docs/02-Game-Design/Outcomes & Feedback.md):
// the dashed lines fade; the selected stars, one after another, swell a
// little and shrink away; each atlas star grows in spinning, overshoots and
// settles with a small burst of sparks; once all have landed the
// constellation's lines draw outward from a random star, wave by wave, each
// wave a little faster; when the word rings a planet its closing line draws
// as one last wave; then its word, then a shine runs along the lines, and
// last the ringed planet spins round once with a bounce. Every time is in milliseconds,
// so the sequence stretches with the word. Pure, tested without a DOM.

export const ACCEPT_MOTION = {
  dashFadeMs: 220,
  /** Between one star's departure and the next. */
  staggerMs: 90,
  /** A selected star swells and shrinks away. */
  departMs: 340,
  /** Its atlas star starts this long after the departure does. */
  arriveLeadMs: 220,
  /** The atlas star grows in spinning, overshoots and settles. */
  arriveMs: 640,
  /** The sparks go off when the star lands, this share of the way through its arrival. */
  landsAt: 0.8,
  sparksMs: 650,
  linesGapMs: 60,
  /** The first wave of lines draws out over this long… */
  lineWaveMs: 280,
  /** …and each wave after it is this much faster. */
  lineWaveSpeedup: 1.1,
  /** The word starts once the last line has reached its star. */
  pillGapMs: 60,
  pillMs: 480,
  /** The shine starts as the word settles. */
  shineLeadMs: 320,
  shineLineMs: 420,
  shineStaggerMs: 110,
  /** The ringed planet spins round once, with a wind-up and an overshoot, this long after the shine has run. */
  spinGapMs: 80,
  spinMs: 950,
} as const;

export type Timing = { delay: number; duration: number };

export interface AcceptTimeline {
  dashFade: Timing;
  depart: (i: number) => Timing;
  arrive: (i: number) => Timing;
  sparks: (i: number) => number;
  /** Wave `k` of lines drawing outward. */
  lineWave: (k: number) => Timing;
  /** The closing line round a ringed planet: the wave after the last; null without a planet. */
  closing: Timing | null;
  pill: Timing;
  shine: (k: number) => Timing;
  /** The ringed planet's spin, after the shine; null without a planet. */
  spin: Timing | null;
  /** When the last thing has finished. */
  total: number;
}

/**
 * The sequence for a word of `stars` nodes whose tree lines draw out in
 * `waves` waves and whose `lines` lines (the closing one included) shine;
 * `planet` when the word rings one.
 */
export function acceptTimeline(stars: number, waves: number, lines: number, planet = false): AcceptTimeline {
  const m = ACCEPT_MOTION;
  const departAt = (i: number) => i * m.staggerMs;
  const arriveAt = (i: number) => departAt(i) + m.arriveLeadMs;
  const landed = Math.max(0, stars - 1) * m.staggerMs + m.arriveLeadMs + m.arriveMs;
  const sparksEnd = arriveAt(Math.max(0, stars - 1)) + m.arriveMs * m.landsAt + m.sparksMs;
  const linesAt = landed + m.linesGapMs;
  const waveMs = (k: number) => m.lineWaveMs / m.lineWaveSpeedup ** k;
  const waveAt = (k: number) => {
    let t = linesAt;
    for (let j = 0; j < k; j++) t += waveMs(j);
    return t;
  };
  const allWaves = waves + (planet ? 1 : 0);
  const pillAt = waveAt(allWaves) + m.pillGapMs;
  const shineAt = pillAt + m.shineLeadMs;
  const shineEnd = lines > 0 ? shineAt + (lines - 1) * m.shineStaggerMs + m.shineLineMs : shineAt;
  const spin = planet ? { delay: shineEnd + m.spinGapMs, duration: m.spinMs } : null;
  return {
    dashFade: { delay: 0, duration: m.dashFadeMs },
    depart: (i) => ({ delay: departAt(i), duration: m.departMs }),
    arrive: (i) => ({ delay: arriveAt(i), duration: m.arriveMs }),
    sparks: (i) => arriveAt(i) + m.arriveMs * m.landsAt,
    lineWave: (k) => ({ delay: waveAt(k), duration: waveMs(k) }),
    closing: planet ? { delay: waveAt(waves), duration: waveMs(waves) } : null,
    pill: { delay: pillAt, duration: m.pillMs },
    shine: (k) => ({ delay: shineAt + k * m.shineStaggerMs, duration: m.shineLineMs }),
    spin,
    total: Math.max(sparksEnd, pillAt + m.pillMs, shineEnd, spin ? spin.delay + spin.duration : 0),
  };
}

export interface LineDraw {
  /** The line's index in the constellation. */
  line: number;
  /** It draws from this node out to that one. */
  from: number;
  to: number;
  wave: number;
}

/**
 * The order the lines draw in: outward from `start`, breadth first. A line
 * leaving a node reached in wave k draws in wave k. A word's lines form a
 * tree (each star after the first joins one earlier star; pass the tree's
 * lines, without a ringed planet's closing line), so every line is reached;
 * anything unreachable would draw in a last wave of its own.
 */
export function lineWaves(lines: ReadonlyArray<readonly [number, number]>, start: number): LineDraw[] {
  const depth = new Map<number, number>([[start, 0]]);
  const queue = [start];
  while (queue.length > 0) {
    const at = queue.shift()!;
    for (const [a, b] of lines) {
      const next = a === at ? b : b === at ? a : null;
      if (next !== null && !depth.has(next)) {
        depth.set(next, depth.get(at)! + 1);
        queue.push(next);
      }
    }
  }
  const reached = Math.max(0, ...depth.values());
  return lines.map(([a, b], line) => {
    const da = depth.get(a) ?? Infinity;
    const db = depth.get(b) ?? Infinity;
    const [from, to] = da <= db ? [a, b] : [b, a];
    const wave = Math.min(da, db);
    return { line, from, to, wave: Number.isFinite(wave) ? wave : reached };
  });
}

/** How many waves the lines draw in. */
export function waveCount(draws: ReadonlyArray<LineDraw>): number {
  return draws.length === 0 ? 0 : Math.max(...draws.map((d) => d.wave)) + 1;
}

/** A star to start from, picked anew for each acceptance but the same on every render of it. */
export function startIndex(seq: number, word: string, count: number): number {
  let h = Math.imul(seq, 2654435761) >>> 0;
  for (let i = 0; i < word.length; i++) h = Math.imul(h ^ word.charCodeAt(i), 16777619) >>> 0;
  return count > 0 ? h % count : 0;
}
